<template>
  <v-card>
    <v-toolbar flat>
      <v-toolbar-title>Csv Datasource [{{ datasource.name }}] for {{ dealerName(datasource) }}</v-toolbar-title>
    </v-toolbar>

    <v-list two-line subheader>
      <v-list-item
        v-for="item in baseInfo"
        :key="item.title"
      >
        <v-list-item-avatar>
          <v-icon :color="item.iconClass">{{ item.icon }}</v-icon>
        </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
          <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
        </v-list-item-content>

      </v-list-item>

      <v-divider inset></v-divider>

      <template v-for="item in detailInfo">
        <template v-if="item.subtitle.isArray">
          <v-list-group
            :key="item.title"
            no-action
            :prepend-icon="item.icon"
            :color="item.iconClass"
          >
            <template v-slot:activator>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <v-list-item
              v-for="subItem in item.subtitle"
              :key="subItem.subtitle"
            >
              <v-list-item-content>
                <v-list-item-title>{{ subItem }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>
        </template>
        <template v-else>
          <v-list-item
            :key="item.title"
          >
            <v-list-item-avatar>
              <v-icon :color="item.iconClass">{{ item.icon }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
            </v-list-item-content>
         </v-list-item>
        </template>
      </template>
   </v-list>
   <v-row justify="center">
     <v-card-actions>
       <v-btn
        v-if="datasource.status === 'complete'"
        color="primary"
        dark
        @click="download">
         Download Complete
         <v-icon right dark>mdi-cloud-download</v-icon>
       </v-btn>
       <v-btn
        v-if="datasource.status !== 'processing'"
        color="orange"
        dark
        @click="downloadOriginal">
         Download Original
         <v-icon right dark>mdi-cloud-download</v-icon>
       </v-btn>
       <v-btn
        v-if="datasource.status === 'complete'"
        color="primary"
        dark
        v-show="!showCsvPreview"
        @click="preview"
       >Preview
         <v-icon right dark>mdi-binoculars</v-icon>
       </v-btn>
       <template v-if="datasource.status === 'complete'">
         <template v-for="doc in additionalDocs">
           <v-btn
            v-bind:key="doc.doc_id"
            color="secondary"
            dark
            @click="doc.action">
             {{ doc.title }}
             <v-icon right dark>{{doc.icon}}</v-icon>
           </v-btn>
         </template>
       </template>

     </v-card-actions>
    </v-row>
     <v-data-table
       v-show="showCsvPreview"
       :loading="previewCsvLoading"
       :headers="csvHeaders"
       :items="csvData"
       item-key="id"
       class="elevation-1"
      >
     </v-data-table>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import axios from 'axios'
import Utils from '@/utils'
import { DatasourceApi } from '@/services'
const Papa = require('papaparse')

function csvParseConfig (vueInstance) {
  const boundThis = vueInstance
  return {
    delimiter: '', // auto-detect
    newline: '', // auto-detect
    quoteChar: '"',
    escapeChar: '"',
    header: false,
    trimHeaders: true,
    dynamicTyping: false,
    preview: 101, // papaparse seems to count the header as 1 - limit to only reading first N records
    encoding: '',
    worker: false,
    comments: false,
    step: undefined,
    complete: function (results) {
      boundThis.$logger.trace('CSV Parse Complete!')
      boundThis.parseResults = results
    },
    error: function (error, file) {
      if (error) {
        this.$logger.error('Error Parsing CSV', error.message)
        boundThis.$flash({
          showClose: true,
          message: 'Error Parsing CSV! ' + error.message
        })
      }
    },
    download: false,
    skipEmptyLines: false,
    chunk: undefined,
    fastMode: undefined,
    beforeFirstChunk: undefined,
    withCredentials: undefined,
    transform: undefined
  }
}

export default {
  computed: {
    ...mapState('DatasourceStore', {
      datasource: 'datasource'
    }),
    baseInfo () {
      if (!this.datasource) {
        return []
      }

      return [
        { icon: 'mdi-information', iconClass: 'blue lighten-1 white--text', title: 'Datasource Name', subtitle: this.datasource.name },
        { icon: 'mdi-car-side', iconClass: 'blue lighten-1 white--text', title: 'Dealer', subtitle: this.dealerName(this.datasource) }
      ]
    },
    detailInfo () {
      if (!this.datasource) {
        return []
      }
      return Object.keys(this.datasource.meta || {}).map((key) => {
        const rowInfo = this.detailInfoRowData[key]
        return Object.assign({}, rowInfo, { title: this.titleForKey(key), subtitle: this.datasource.meta[key] })
      })
    },
    csvHeaders () {
      return Utils.nestedPropertyOr([], ['data', 0], this.parseResults).map(col => {
        return { text: col, value: col }
      })
    },
    csvData () {
      const _rows = Utils.nestedPropertyOr([], ['data'], this.parseResults)
      const rowList = []
      for (let rowIdx = 1; rowIdx < _rows.length; rowIdx++) {
        let rowObject = {}
        for (let colIdx = 0; colIdx < _rows[rowIdx].length; colIdx++) {
          rowObject[Utils.nestedPropertyOr('Unknown', [colIdx, 'value'], this.csvHeaders)] = Utils.nestedPropertyOr('', [rowIdx, colIdx], _rows).toString()
        }
        rowList.push(rowObject)
      }
      return rowList
    },
    additionalDocs () {
      return (this.datasource.params.additional_docs || []).map(doc => {
        return { title: doc.description, action: function () { return this.downloadAdditional(doc.doc_id) }.bind(this), icon: this.documentIcon(doc.type) }
      })
    }
  },
  mounted: function () {
    this.$store.dispatch('DatasourceStore/getDatasource', { id: this.$route.params.id })
      .catch(error => {
        this.$logger.error('Error fetching datasource', error.message)
        this.$flash({
          showClose: true,
          message: 'Error loading Datasource. Reload to try again',
          type: 'error'
        })
      })
  },
  data () {
    return {
      colorByStatus: {
        complete: 'green',
        processing: 'orange',
        error: 'red'
      },
      detailInfoRowData: {
        row_count: { icon: 'mdi-format-list-bulleted', iconClass: 'green white--text' },
        headers: { icon: 'mdi-format-columns', iconClass: 'green white--text' },
        errors: { icon: 'mdi-alert', iconClass: 'red white--text' }
      },
      showCsvPreview: false,
      previewCsvLoading: false,
      parseResults: null
    }
  },
  methods: {
    titleForKey (key) {
      return key.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    },
    async _downloadUrl (actionName, item, optionalDocId) {
      try {
        const url = await DatasourceApi[actionName](item, optionalDocId)
        const downloadLink = document.createElement('a')
        downloadLink.setAttribute('href', url)
        downloadLink.setAttribute('style', 'display:none')
        downloadLink.setAttribute('target', '_blank')
        document.body.append(downloadLink)

        setTimeout(function () {
          downloadLink.click()
          downloadLink.remove()
        }, null)
      } catch (error) {
        this.$flash({
          showClose: true,
          message: 'Error downloading CSV File! ' + error.message,
          type: 'error'
        })
      }
    },
    async download () {
      await this._downloadUrl('getCsvFinalDocumentUrl', this.datasource)
    },
    async downloadOriginal () {
      await this._downloadUrl('getCsvOriginalDocumentUrl', this.datasource)
    },
    async downloadAdditional (docId) {
      await this._downloadUrl('getCsvAdditionalDocumentUrl', this.datasource, docId)
    },
    async parseCsvData (rawCsvData) {
      const that = this
      Papa.parse(rawCsvData, csvParseConfig(that))
    },
    async showPreview () {
      this.showCsvPreview = true
    },
    hideCsvPreview () {
      this.showCsvPreview = false
    },
    async preview () {
      try {
        this.previewCsvLoading = true
        await this.showPreview()
        const url = await DatasourceApi.getCsvFinalDocumentUrl(this.datasource)
        const req = await axios.get(url)
        await this.parseCsvData(req.data)
      } catch (error) {
        this.$logger.error('Error Previewing CSV Data', error.message)
        this.$flash({
          showClose: true,
          message: 'Error Previewing CSV Data! ' + error.message,
          type: 'error'
        })
      } finally {
        this.previewCsvLoading = false
      }
    },
    dealerName (datasource) {
      if (datasource && datasource.dealer) {
        return datasource.dealer.name
      } else {
        return ''
      }
    },
    documentIcon (docType) {
      return {
        excel: 'mdi-microsoft-excel',
        xlsx: 'mdi-microsoft-excel'
      }[docType]
    }
  }
}
</script>
